import { useMutateDeleteEmptySessions } from '@/hooks/mutation/useMutateDeleteEmptySessions';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { Key } from 'ts-key-enum';

import { deleteSessionDataAtom } from '@/components/modal/delete-session-modal.atoms';
import { useQueryCombinedSessions } from '@/hooks/query/useQueryCombinedSessions';
import {
  isSessionsSelectedAllAtom,
  lastSelectedIndexAtom,
  selectedSessionIdsAtom,
} from '@heidi/session';
import { isHotkeyPressed } from 'react-hotkeys-hook';
import { useSessionDeletedRecovery } from './useSessionDeletedRecovery';

export {
  isSessionsSelectedAllAtom,
  lastSelectedIndexAtom,
  selectedSessionIdsAtom,
} from '@heidi/session';

export const useSessionsList = () => {
  const [selectedSessionIds, setSelectedSessionIds] = useAtom(selectedSessionIdsAtom);
  const [lastSelectedIndex, setLastSelectedIndex] = useAtom(lastSelectedIndexAtom);
  const [selectedAll, setSelectedAll] = useAtom(isSessionsSelectedAllAtom);
  const onSessionDeleted = useSessionDeletedRecovery();

  const {
    sessions,
    loadedSessionCount,
    isError,
    isFetching,
    isLoading,
    isRefetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch: refetchSessions,
  } = useQueryCombinedSessions();

  const { mutateAsync: onDeleteEmptySessions } = useMutateDeleteEmptySessions();
  const onDeleteEmptySessionClick = useCallback(async () => {
    await onDeleteEmptySessions();
    onSessionDeleted();
  }, [onDeleteEmptySessions, onSessionDeleted]);

  const allSessionIds = useMemo(
    () =>
      sessions
        ?.filter((session) => !session.is_offline_synthetic)
        .map((session) => session.session_id) || [],
    [sessions]
  );

  const handleSessionSelection = useCallback(
    (sessionId: string, selected: boolean) => {
      if (!allSessionIds.includes(sessionId)) return;

      setSelectedSessionIds((prev) => {
        const newSelected = [...(prev || [])];
        const currentIndex = allSessionIds.indexOf(sessionId);
        if (isHotkeyPressed(Key.Shift) && lastSelectedIndex !== null) {
          const start = Math.min(lastSelectedIndex, currentIndex);
          const end = Math.max(lastSelectedIndex, currentIndex);

          for (let i = start; i <= end; i++) {
            const sessionId = allSessionIds.at(i);
            if (sessionId !== undefined) {
              if (selected && !newSelected.includes(sessionId)) {
                newSelected.push(sessionId);
              } else if (!selected) {
                const index = newSelected.indexOf(sessionId);
                if (index > -1) {
                  newSelected.splice(index, 1);
                }
              }
            }
          }
        } else {
          if (selected) {
            newSelected.push(sessionId);
          } else {
            const index = newSelected.indexOf(sessionId);
            if (index > -1) {
              newSelected.splice(index, 1);
            }
          }
        }

        setLastSelectedIndex(currentIndex);
        return newSelected;
      });
    },
    [allSessionIds, lastSelectedIndex, setLastSelectedIndex, setSelectedSessionIds]
  );

  const onRefetchBtnClick = useCallback(() => {
    void refetchSessions();
  }, [refetchSessions]);

  const setDeleteSessionData = useSetAtom(deleteSessionDataAtom);
  const handleDeleteSelected = useCallback(() => {
    const sessionsToDelete = selectedSessionIds.filter((sessionId) =>
      allSessionIds.includes(sessionId)
    );
    if (sessionsToDelete.length === 0) return;

    setDeleteSessionData({
      sessionsToDelete,
      selectedAll: selectedAll && sessionsToDelete.length === allSessionIds.length,
      onSessionsDeleted: onSessionDeleted,
    });
  }, [
    allSessionIds,
    setDeleteSessionData,
    selectedSessionIds,
    selectedAll,
    onSessionDeleted,
  ]);

  const toggleSelectedAll = useCallback(() => {
    if (!selectedAll) {
      setSelectedAll(true);
    } else {
      setSelectedAll(false);
      setSelectedSessionIds([]);
    }
  }, [selectedAll, setSelectedAll, setSelectedSessionIds]);

  return {
    sessions,
    loadedSessionCount,
    isError,
    onSessionDeleted,
    onDeleteEmptySessionClick,
    handleSessionSelection,
    onRefetchBtnClick,
    handleDeleteSelected,
    allSessionIds,
    toggleSelectedAll,
    isFetching,
    isRefetching,
    isFetchingNextPage,
    isLoading,
    fetchNextPage,
    hasNextPage,
  };
};
